import React from 'react'
import { Tabs } from '@base-ui/react/tabs'
import { styled } from '@mui/material'
import { useScreenSize } from 'src/customHooks/customHooks'

const TabsRoot = styled(Tabs.Root, {
  shouldForwardProp: (prop) => prop !== 'alignContentInColumnForm',
})(
  ({ theme, alignContentInColumnForm = false }) => `
  border-radius: 0.375rem;
  height: ${alignContentInColumnForm ? '100%' : 'unset'};
`,
)

const TabsList = styled(Tabs.List, {
  shouldForwardProp: (prop) => prop !== 'alignContentInColumnForm',
})(
  ({ theme, alignContentInColumnForm = false }) => `
  display: flex;
  position: relative;
  align-items: center;
  z-index: 0;
  padding-inline: 0.25rem;
  gap: 0.5rem;
  height: ${alignContentInColumnForm ? '100%' : '2.5rem'};
  width: 100%;
  // box-shadow: inset 0 -1px var(--color-gray-200);
`,
)

const Tab = styled(Tabs.Tab, {
  shouldForwardProp: (prop) => 
    prop !== 'alignContentInColumnForm' && 
    prop !== 'selectedColor' && 
    prop !== 'hoverItemColor' && 
    prop !== 'unselectedColor',
})(
  ({
    theme,
    alignContentInColumnForm = false,
    selectedColor = 'var(--color-gray-900)',
    hoverItemColor = 'var(--color-gray-900)',
    unselectedColor = 'white',
  }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction:${alignContentInColumnForm ? 'column' : 'row'};
  border: 0;
  margin: 0;
  outline: 0;
  background: none;
  appearance: none;
  color: ${unselectedColor};
  font-family: 'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  user-select: none;
  padding-inline: 0.75rem;
  padding-block: 0.5rem;
  height: 2.5rem;
  gap: ${alignContentInColumnForm ? theme.spacing(0.5) : '0.5rem'};
  border-radius: 0.5rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &[data-selected] {
    color: ${selectedColor};
    background-color: rgba(16, 185, 129, 0.1);
    font-weight: 600;
  }

  @media (hover: hover) {
    &:hover:not([data-selected]) {
      color: ${hoverItemColor};
      background-color: rgba(16, 185, 129, 0.05);
    }
  }

  &:focus-visible {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      inset: 0.125rem;
      border-radius: 0.375rem;
      outline: 2px solid rgba(16, 185, 129, 0.5);
      outline-offset: -1px;
    }
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: inherit;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding-inline: 0.625rem;
    font-size: 0.8125rem;
    gap: 0.375rem;
    
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`,
)

function getIndicatorHeight({ variant }) {
  if (variant === 'contained') {
    return '100%'
  }
  if (variant === 'underlined') {
    return '3px'
  }
  if (variant === 'aligned') {
    return '100%'
  }
}

const Indicator = styled(Tabs.Indicator)(
  ({ theme, color = 'primary', variant = 'contained' }) => `
  position: absolute;
  z-index: -1;
  left: 0;
  top: ${variant !== 'contained' ? '100%' : '50%'};
  translate: var(--active-tab-left) -50%;
  width: var(--active-tab-width);
  height: ${getIndicatorHeight({ variant })};
  border-radius: ${variant === 'contained' ? '0.5rem' : '0.25rem'};
  background-color: ${variant === 'contained' 
    ? 'rgba(16, 185, 129, 0.15)' 
    : (theme.palette?.[color]?.main || 'rgba(16, 185, 129, 0.8)')};
  transition-property: translate, width;
  transition-duration: 250ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  ${variant === 'contained' ? 'box-shadow: 0 1px 3px rgba(16, 185, 129, 0.2);' : ''}
`,
)

export default function BaseUITabsComponent({
  tabs = [],
  value,
  onChange,
  classNames = {},
  getTabId = (tab) => tab?.id,
  getTabLabel = (tab) => tab?.title,
  getTabIcon = (tab) => tab?.icon,
  incicatorColor = 'primary',
  alignContentInColumnForm = false,
  variant = 'contained',
  unselectedColor = 'white',
  selectedColor = 'white',
  hoverItemColor = 'var(--color-gray-900)',
  hideTextInMobileMode,
}) {
  const { width } = useScreenSize()
  const isMobile = width < 768
  const hideText = hideTextInMobileMode && isMobile

  return (
    <TabsRoot
      value={value}
      onValueChange={onChange}
      className={classNames.root}
      alignContentInColumnForm={alignContentInColumnForm}
    >
      <TabsList alignContentInColumnForm={alignContentInColumnForm} className={classNames.list}>
        {tabs.map((tab) => {
          const label = getTabLabel(tab)
          const id = getTabId(tab)
          const Icon = getTabIcon(tab)
          return (
            <Tab
              key={id}
              value={id}
              className={classNames.tab}
              alignContentInColumnForm={alignContentInColumnForm}
              selectedColor={selectedColor}
              unselectedColor={unselectedColor}
              hoverItemColor={hoverItemColor}
              onChange={onChange}
              data-state={value === id ? 'active' : ''}
            >
              {Icon && <Icon />}
              {!hideText && label}
            </Tab>
          )
        })}
        <Indicator variant={variant} color={incicatorColor} className={classNames.indicator} />
      </TabsList>
    </TabsRoot>
  )
}
